import { useIsMounted } from '@maternity/mun-cantrips';
import { FormApi, useField } from '@maternity/mun-forms';
import { Spinner } from '@maternity/mun-spinner';
import * as braintree from 'braintree-web';
import * as React from 'react';

const fields = ['number', 'expirationDate', 'cvv'] as const;
export type FieldName = (typeof fields)[number];
export const getModel = (field: FieldName) => `braintree.${field}` as const;

type State = 'loading' | 'error' | 'success';

// The braintree HostedField types aren't exactly right and we need to 1) use
// `container` instead of `selector` in fields options and 2) add an `off`
// method to HostedFields
type HostedFieldFieldOptions = {
  [P in keyof braintree.HostedFieldFieldOptions]: Omit<
    braintree.HostedFieldFieldOptions[P],
    'selector'
  > & {
    container: string | HTMLElement;
  };
};

type HostedFields = braintree.HostedFields & {
  off: braintree.HostedFields['on'];
  create(options: {
    client?: braintree.Client;
    authorization?: string;
    fields: HostedFieldFieldOptions;
    styles?: any;
  }): Promise<HostedFields>;
};

export type GetNonce = () => Promise<string | undefined>;

interface Props {
  // The braintree fields are "virtual", so this component doesn't need to
  // constrain the FieldValues
  form: FormApi<any>;
  token: string;
  getNonceRef: React.MutableRefObject<GetNonce>;
  onCardTypeChange: (cardType?: string) => void;
  children?: React.ReactNode;
}

/**
 * Connects a braintree client to the parent form.
 *
 * The necessary `HostedField` components should be included in `children`.
 */
export const BraintreeClient = ({
  form,
  token,
  getNonceRef,
  onCardTypeChange,
  children,
}: Props) => {
  const isMounted = useIsMounted();

  const [state, setState] = React.useState<State>('loading');
  const hostedFields = React.useRef<HostedFields>();

  getNonceRef.current = React.useCallback(async () => {
    const payload = await hostedFields.current?.tokenize();
    return payload?.nonce;
  }, []);

  // Register fields
  // Because we are iterating over a readonly array we can be confident that the
  // hooks are being called in the same order each render and safely disable the
  // lint rule
  for (const field of fields) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const validators = React.useMemo(
      () => [
        () => {
          const hostedFieldsState:
            | braintree.HostedFieldsStateObject
            | undefined = hostedFields.current?.getState();
          if (!hostedFieldsState) return;
          const hostedField = hostedFieldsState.fields[field];
          if (hostedField.isEmpty === true) {
            return 'required';
          } else if (hostedField.isValid !== true) {
            return 'invalid_braintree';
          }
        },
      ],
      [field, hostedFields],
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useField({ form, model: getModel(field), validators });
  }

  // Create Hosted Fields
  React.useEffect(() => {
    const handleBlur = (event: braintree.HostedFieldsStateObject) => {
      const field = getModel(event.emittedBy as FieldName);
      form.setFieldTouched(field);
    };
    const handleValidityChange = (event: braintree.HostedFieldsStateObject) => {
      const field = getModel(event.emittedBy as FieldName);
      // Set a value to trigger validation
      form.setFieldValue(field, undefined as any);
    };
    const handleCardTypeChange = (event: braintree.HostedFieldsStateObject) => {
      if (event.cards.length === 1) {
        onCardTypeChange(event.cards[0].niceType);
      } else {
        onCardTypeChange();
      }
    };
    const getClient = async () => {
      try {
        if (!hostedFields.current) {
          const client = await braintree.client.create({
            authorization: token,
          });
          const options = {
            client,
            styles: {
              input: 'braintree-hosted-fields',
            },
            fields: {
              number: {
                container: "[braintree-field='number']",
              },
              expirationDate: {
                container: "[braintree-field='expirationDate']",
                placeholder: 'MM/YYYY',
              },
              cvv: {
                container: "[braintree-field='cvv']",
              },
            },
          };
          hostedFields.current = await (
            braintree.hostedFields as HostedFields
          ).create(options);
        }
        if (isMounted()) {
          setState('success');
          hostedFields.current.on('blur', handleBlur);
          hostedFields.current.on('validityChange', handleValidityChange);
          hostedFields.current.on('cardTypeChange', handleCardTypeChange);
        }
      } catch (error) {
        if (isMounted()) {
          setState('error');
        }
      }
    };

    getClient();
    return () => {
      if (hostedFields.current) {
        hostedFields.current.off('blur', handleBlur);
        hostedFields.current.off('validityChange', handleValidityChange);
        hostedFields.current.off('cardTypeChange', handleCardTypeChange);
      }
    };
  }, [form, onCardTypeChange, token, isMounted, hostedFields]);
  // Teardown on unmount
  React.useEffect(
    () => () => {
      hostedFields.current?.teardown();
      // Replace nonce function with no-op in case something calls it
      getNonceRef.current = async () => undefined;
    },
    [hostedFields, getNonceRef],
  );

  return state === 'error' ? (
    <p className="braintree-error">
      We're sorry, there was a problem loading the form. Please try reloading
      the page.
    </p>
  ) : (
    <React.Fragment>
      {state === 'loading' && <Spinner />}
      <div style={{ display: state === 'loading' ? 'none' : 'inherit' }}>
        {children}
      </div>
    </React.Fragment>
  );
};
